import { BASE_URL_APP } from '../http/baseUrl'

const selectors = {
    list: '[data-advanced-category-list]',
    item: '[data-advanced-category-item]',
    panel: '[data-advanced-category-panel]',
    pendingPanel: '[data-menu-panel-src]',
    pendingSubmenu: '[data-menu-submenu-src]',
    itemStyleReference: '[data-category-style-reference="item"]',
    panelStyleReference: '[data-category-style-reference="panel"]',
}

// getEndPointBaseUrl() devolve "/", "/loja/" ou "/beta/" conforme o tenant, e em
// desenvolvimento o app roda sob /fflip/. Um caminho fixo "/menu/categorias" so
// resolve no tenant que fica na raiz do dominio; nos demais o fetch cai em 404 e
// os submenus nunca chegam. BASE_URL_APP sempre termina em barra.
const FRAGMENTS_URL = `${BASE_URL_APP}menu/categorias`

let closeTimer = null
let fragmentsRequest = null
let styleClasses = null
let hoveredItem = null

const panelFragments = new Map()
const submenuFragments = new Map()

const hasPendingFragments = () => {
    return document.querySelector(
        `${selectors.pendingPanel}, ${selectors.pendingSubmenu}`
    ) !== null
}

/**
 * O HTML dos submenus chega depois que o Vue ja montou, entao nao passa pelo
 * compilador e nao ganha as classes do styled-components. Copiamos as classes
 * dos elementos de referencia que ficaram no HTML inicial.
 */
const getStyleClasses = () => {
    if (styleClasses) {
        return styleClasses
    }

    const item = document.querySelector(selectors.itemStyleReference)
    const panel = document.querySelector(selectors.panelStyleReference)

    const classes = {
        item: item ? item.className : '',
        panel: panel ? panel.className : '',
    }

    // So memoriza depois que o Vue montou e as classes existem de fato.
    if (classes.item && classes.panel) {
        styleClasses = classes
    }

    return classes
}

const applyFragments = () => {
    const classes = getStyleClasses()

    document.querySelectorAll(selectors.pendingPanel).forEach((placeholder) => {
        const html = panelFragments.get(placeholder.dataset.menuPanelSrc)

        if (html === undefined) {
            return
        }

        placeholder.innerHTML = html
        placeholder.removeAttribute('data-menu-panel-src')
    })

    document.querySelectorAll(selectors.pendingSubmenu).forEach((placeholder) => {
        const html = submenuFragments.get(placeholder.dataset.menuSubmenuSrc)

        if (html === undefined) {
            return
        }

        placeholder.innerHTML = html
        placeholder.removeAttribute('data-menu-submenu-src')

        if (classes.item) {
            placeholder.querySelectorAll('.js-category-item').forEach((node) => {
                node.className = classes.item
            })
        }

        if (classes.panel) {
            placeholder.querySelectorAll('.js-category-panel').forEach((node) => {
                node.className = classes.panel
            })
        }
    })
}

const loadFragments = () => {
    if (fragmentsRequest) {
        return fragmentsRequest
    }

    if (!hasPendingFragments()) {
        fragmentsRequest = Promise.resolve()
        return fragmentsRequest
    }

    fragmentsRequest = fetch(FRAGMENTS_URL, { credentials: 'same-origin' })
        .then((response) => {
            if (!response.ok) {
                throw new Error(`menu fragments: ${response.status}`)
            }

            return response.text()
        })
        .then((html) => {
            const parsed = new DOMParser().parseFromString(html, 'text/html')

            parsed.querySelectorAll('template[data-menu-panel]').forEach((node) => {
                panelFragments.set(node.dataset.menuPanel, node.innerHTML)
            })

            parsed.querySelectorAll('template[data-menu-submenu]').forEach((node) => {
                submenuFragments.set(node.dataset.menuSubmenu, node.innerHTML)
            })

            applyFragments()
        })
        .catch(() => {
            // Deixa uma nova tentativa possivel no proximo hover.
            fragmentsRequest = null
        })

    return fragmentsRequest
}

const closePanels = () => {
    document.querySelectorAll(`${selectors.panel}.is-open`).forEach((panel) => {
        panel.classList.remove('is-open')
    })
}

const cancelClose = () => {
    if (!closeTimer) {
        return
    }

    clearTimeout(closeTimer)
    closeTimer = null
}

const scheduleClose = () => {
    cancelClose()
    closeTimer = setTimeout(closePanels, 400)
}

const getPanelMaxHeight = (panel) => {
    const maxHeight = parseInt(window.getComputedStyle(panel).maxHeight, 10)
    return Number.isNaN(maxHeight) ? 420 : maxHeight
}

const getPanelHeight = (panel) => {
    const maxHeight = getPanelMaxHeight(panel)
    const previousDisplay = panel.style.display
    const previousVisibility = panel.style.visibility
    const previousPointerEvents = panel.style.pointerEvents

    panel.style.display = 'block'
    panel.style.visibility = 'hidden'
    panel.style.pointerEvents = 'none'

    const measuredHeight = panel.scrollHeight || panel.offsetHeight || maxHeight

    panel.style.display = previousDisplay
    panel.style.visibility = previousVisibility
    panel.style.pointerEvents = previousPointerEvents

    return Math.min(measuredHeight, maxHeight)
}

const positionPanel = (panel, list, item) => {
    const listRect = list.getBoundingClientRect()
    const itemRect = item.getBoundingClientRect()
    const shouldAlignToItem = list.dataset.categoryPanelPosition === 'item'
    const panelWidth = parseInt(window.getComputedStyle(panel).width, 10) || 720
    const panelHeight = shouldAlignToItem ? getPanelHeight(panel) : getPanelMaxHeight(panel)
    const gap = 0
    const baseTop = shouldAlignToItem ? itemRect.top : listRect.top
    const rightSideLeft = listRect.right + gap
    const leftSideLeft = listRect.left - panelWidth - gap
    const left = rightSideLeft + panelWidth <= window.innerWidth - 8
        ? rightSideLeft
        : leftSideLeft >= 8
            ? leftSideLeft
            : window.innerWidth - panelWidth - 8
    const top = Math.min(baseTop, window.innerHeight - panelHeight - 8)

    panel.style.setProperty('--category-advanced-left', `${Math.max(left, 8)}px`)
    panel.style.setProperty('--category-advanced-top', `${Math.max(top, 8)}px`)
}

const showPanel = (item, panel, list) => {
    positionPanel(panel, list, item)
    closePanels()
    panel.classList.add('is-open')
}

const openPanel = (item) => {
    const panel = item.querySelector(selectors.panel)
    const list = item.closest(selectors.list)

    if (!panel || !list) {
        closePanels()
        return
    }

    cancelClose()

    // Painel ainda sem conteudo: busca e so abre se o ponteiro continuar nele.
    if (panel.hasAttribute('data-menu-panel-src')) {
        loadFragments().then(() => {
            if (hoveredItem === item) {
                showPanel(item, panel, list)
            }
        })

        return
    }

    showPanel(item, panel, list)
}

/**
 * Comeca a busca cedo, sem competir com o carregamento inicial: na primeira
 * interacao do visitante ou na primeira ociosidade depois do load.
 */
const scheduleFragmentsLoad = () => {
    if (!hasPendingFragments()) {
        return
    }

    const events = ['pointerover', 'pointerdown', 'keydown', 'touchstart']
    const start = () => {
        events.forEach((event) => window.removeEventListener(event, start))
        loadFragments()
    }

    events.forEach((event) => {
        window.addEventListener(event, start, { once: true, passive: true })
    })

    window.addEventListener('load', () => {
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(start, { timeout: 4000 })
        } else {
            window.setTimeout(start, 2500)
        }
    })
}

export const installAdvancedCategoryMenu = () => {
    scheduleFragmentsLoad()

    document.addEventListener('mouseover', (event) => {
        const item = event.target.closest(selectors.item)

        hoveredItem = item

        if (item) {
            openPanel(item)
            return
        }

        if (
            event.target.closest(selectors.list) ||
            event.target.closest(selectors.panel)
        ) {
            cancelClose()
            return
        }

        scheduleClose()
    })

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            closePanels()
        }
    })

    window.addEventListener('resize', closePanels)
    window.addEventListener('scroll', (event) => {
        if (event.target?.closest?.(selectors.list)) {
            return
        }

        closePanels()
    }, true)
}
